// table-widget.component.ts
import {
  Component, Input, OnChanges, SimpleChanges,
  ViewChild, AfterViewInit, ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { TableConfig, WidgetRow } from '../Services/widget.models';

@Component({
  selector: 'app-table-widget',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatTableModule, MatSortModule, MatPaginatorModule,
    MatInputModule, MatFormFieldModule,
    MatIconModule, MatTooltipModule, MatButtonModule, MatChipsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="table-shell" [ngStyle]="shellStyles">

      <!-- Header row: title + optional search -->
      <div class="table-top" *ngIf="config.title || config.showFilter">
        <span class="table-title" *ngIf="config.title">{{ config.title }}</span>
        <mat-form-field appearance="outline" class="filter-field" *ngIf="config.showFilter">
          <mat-label>Search</mat-label>
          <input matInput [(ngModel)]="filterValue" (ngModelChange)="applyFilter($event)" />
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>
      </div>

      <!-- No data -->
      <div class="no-data" *ngIf="!dataSource.data.length">
        <mat-icon>inbox</mat-icon>
        <span>No data available</span>
      </div>

      <!-- Table -->
      <div class="table-scroll" *ngIf="dataSource.data.length">
        <table mat-table [dataSource]="dataSource"
               matSort
               class="w-100 widget-table">

          <!-- Dynamic columns -->
          <ng-container *ngFor="let col of config.columns"
                        [matColumnDef]="col.key">

            <!-- Header -->
            <th mat-header-cell *matHeaderCellDef
                [mat-sort-header]="col.sortable ? col.key : ''"
                [style.width]="col.width || 'auto'">
              {{ langCode === 'ar' ? (col.labelAr || col.labelEn) : col.labelEn }}
            </th>

            <!-- Cell -->
            <td mat-cell *matCellDef="let row">

              <!-- date -->
              <ng-container *ngIf="col.type === 'date'">
                {{ row[col.key] | date: (col.dateFormat || 'dd/MM/yyyy') }}
              </ng-container>

              <!-- badge -->
              <ng-container *ngIf="col.type === 'badge'">
                <span class="badge"
                      [style.background]="col.badgeColorFn ? col.badgeColorFn(row) : '#e0e0e0'">
                  {{ row[col.key] ?? '—' }}
                </span>
              </ng-container>

              <!-- action -->
              <ng-container *ngIf="col.type === 'action'">
                <button mat-icon-button
                        [matTooltip]="col.actionTooltip || ''"
                        (click)="onAction(col.key, row)">
                  <mat-icon>{{ col.actionIcon || 'more_vert' }}</mat-icon>
                </button>
              </ng-container>

              <!-- default text / number -->
              <ng-container *ngIf="!col.type || col.type === 'text' || col.type === 'number'">
                {{ row[col.key] ?? '—' }}
              </ng-container>

            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"
              class="widget-row"></tr>
        </table>
      </div>

      <div class="paginator-wrapper">
      <mat-paginator
        [pageSize]="config.pageSize || 50"
        [pageSizeOptions]="config.pageSizeOptions || [5,10,25,50]"
        showFirstLastButtons>
      </mat-paginator>
      </div>

    </div>
  `,
  styles: [`
    :host { display: block; }

    .table-shell {
      width: 100%;
      height: 100%;
      // background: #fff;
      background-color: var(--dsh-primary, #fff);
      border-radius: 16px;
      box-shadow: 0 2px 12px rgba(0,0,0,.08);
      padding: 18px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      overflow: hidden;
    }

    .table-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px;
    }
    .table-title {
      font-size: 15px;
      font-weight: 600;
      // color: #2a3547;
      color: var(--dsh-text, #0c0c0c);
    }
    .filter-field { width: 240px; }

    .no-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 0;
      // color: #9aa5b4;
      color: var(--dsh-text, #0c0c0c);
      gap: 8px;
      mat-icon { font-size: 36px; width: 36px; height: 36px; }
      span { font-size: 14px; }
    }

    .table-scroll { overflow-x: auto; }

    .widget-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
    }
    .widget-table th {
      // background: #f6f8fb;
      background: var(--dsh-accent, #fff);
      // color: #5a6a85;
      color: var(--dsh-text, #0c0c0c);
      font-size: 12px;
      font-weight: 700;
      letter-spacing: .4px;
      text-transform: uppercase;
      border-bottom: 1px solid var(--dsh-accent, #fff);
      padding: 10px 14px;
    }
    .widget-table td {
      font-size: 13px;
      // color: #2a3547;
      color: var(--dsh-text, #0c0c0c);
      padding: 10px 14px;
      border-bottom: 1px solid var(--dsh-accent, #fff);
      background: var(--dsh-primary, #fff);
    }
    
    .widget-row:last-child td { border-bottom: none; }
    
    .widget-row:hover td { 
      // background: #f6f9ff;
      background: var(--dsh-accent, #fff); 
    }

    .badge {
      display: inline-block;
      border-radius: 20px;
      padding: 2px 10px;
      font-size: 11px;
      font-weight: 600;
      // color: #fff;
      color: var(--dsh-text, #0c0c0c);
    }
    .paginator-wrapper {
  background-color: var(--dsh-accent, #fff);
  color: inherit;
}

/* Main paginator container */
.paginator-wrapper .mat-mdc-paginator {
  background-color: inherit !important;
  color: inherit !important;
}

/* Text (range label, page size label) */
.paginator-wrapper .mat-mdc-paginator-range-label,
.paginator-wrapper .mat-mdc-paginator-page-size-label {
  color: inherit !important;
}

/* Dropdown (page size select) */
.paginator-wrapper .mat-mdc-select-value,
.paginator-wrapper .mat-mdc-select-arrow {
  color: inherit !important;
}

/* Icons (next/prev buttons) */
.paginator-wrapper button {
  color: inherit !important;
}

/* Optional: remove default divider line */
.paginator-wrapper .mat-mdc-paginator {
  border-top: none;
}
  `]
})
export class TableWidgetComponent implements OnChanges, AfterViewInit {
  @Input() config!: TableConfig;
  @ViewChild(MatSort)      sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<WidgetRow>([]);
  displayedColumns: string[] = [];
  filterValue = '';
  shellStyles: Record<string, string> = {};

  get langCode(): string { return this.config?.langCode ?? 'en'; }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    if (this.config?.sortActive) {
      this.sort.active = this.config.sortActive;
      this.sort.direction = this.config.sortDirection ?? 'asc';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config) {
      this.displayedColumns = this.config.columns.map(c => c.key);
      this.dataSource.data = this.config.data ?? [];
      this.shellStyles = {
        ...(this.config.width  ? { width:  this.config.width  } : {}),
        ...(this.config.height ? { height: this.config.height } : {}),
      };
    }
  }

  applyFilter(value: string): void {
    this.dataSource.filter = value.trim().toLowerCase();
    this.dataSource.paginator?.firstPage();
  }

  onAction(key: string, row: WidgetRow): void {
    // Emit via EventEmitter if needed — extend per use case
  }
}
